import { useForm } from '@deadcow-enterprises/react-forms';
import { CodeInputLight } from '@/components/code-input/code-input.light';
import type { ContactType } from '@/components/contact-selection/props';
import { useSendCode, useValidateCode } from '@/hooks/api/contact-validation.hooks';
import { logger, useI18n, widgets } from '@nosinovacao/nosid-mfe-common';
import z from 'zod';
import type { SettingsStep, StepNavigation } from './models';

interface CodeInputParams {
  contactType: ContactType;
  targetId: string;
  onSuccessStep?: SettingsStep;
}

export function SettingsCodeInputSection({
  goToStep,
  params,
}: Readonly<StepNavigation<SettingsStep, CodeInputParams>>) {
  const contactType = params?.contactType ?? 'Sms';
  const targetId = params?.targetId ?? '';
  const onSuccessStep = params?.onSuccessStep ?? 'delete-account-confirm';
  const { t } = useI18n();

  const form = useForm<{ code: string }>(
    {
      code: {
        value: '',
        id: 'contact-validation-otp',
        validator: z
          .string()
          .length(
            widgets.ValidationCodeLength,
            t('Common.ContactValidation.CodeInput.Errors.InvalidCode').toString(),
          ),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  const { sendCode, isSending, captcha } = useSendCode();
  const { validateCode, isValidating } = useValidateCode({
    onSuccess: () => {
      goToStep(onSuccessStep);
    },
    onError: () => {
      form.addError('code', t('Common.ContactValidation.CodeInput.Errors.InvalidCode').toString());
      form.setValue('code', '');
    },
  });

  const handleResend = () => {
    form.setValue('code', '');
    if (captcha.challenge && !captcha.token) return;
    sendCode({ targetId, type: contactType, resend: true, captcha });
  };

  const handleSubmit = ({ code }: typeof form.value) => {
    validateCode({ code, targetId, contactType });
  };

  const handleCodeChange = (value: string) => {
    form.setValue('code', value);
    if (value.length === widgets.ValidationCodeLength) {
      form.updateValidity();
      validateCode({ code: value, targetId, contactType });
    }
  };

  return (
    <CodeInputLight
      codeValue={form.value.code}
      codeId={form.ids.code ?? 'contact-validation-otp'}
      codeErrors={form.errors.code}
      isValidating={isValidating}
      isSending={isSending}
      captchaChallenge={captcha.challenge}
      onCaptchaChange={captcha.setToken}
      onCodeChange={handleCodeChange}
      onResend={handleResend}
      onSubmit={form.handleSubmit(handleSubmit)}
    />
  );
}
